import React, { memo, useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Modal from '../../../components/ui/Modal';
import { supabase } from '../../../utils/supabase';
import { regeneratePhotoUrl } from '../../../services/checkInService';

const TimesheetEntryCard = memo(({ entry, contracts, onUpdate }) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [checkInPhoto, setCheckInPhoto] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [failedImage, setFailedImage] = useState(false);
  const [refreshingUrl, setRefreshingUrl] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Fetch check-in photo when modal opens
  useEffect(() => {
    if (showDetailModal && entry) {
      fetchCheckInPhoto();
    }
  }, [showDetailModal, entry]);

  const fetchCheckInPhoto = async () => {
    setLoadingImage(false);
    setFailedImage(false);
    setCheckInPhoto(null);
    
    try {
      let photoData = null;

      // Method 1: Try fetching via shift_id if available
      if (entry.shift_id) {
        const { data, error } = await supabase
          .from('va_checkins')
          .select('photo_url, photo_storage_path')
          .eq('shift_id', entry.shift_id)
          .order('check_in_time', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          photoData = data;
        }
      }

      // Method 2: Fallback - try fetching by contract_id and date range
      if (!photoData && entry.contract_id) {
        const entryDate = new Date(entry.start_time);
        const dayStart = new Date(
          entryDate.getFullYear(),
          entryDate.getMonth(),
          entryDate.getDate()
        );
        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);

        const { data, error } = await supabase
          .from('va_checkins')
          .select('photo_url, photo_storage_path')
          .eq('contract_id', entry.contract_id)
          .gte('check_in_time', dayStart.toISOString())
          .lt('check_in_time', dayEnd.toISOString())
          .order('check_in_time', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          photoData = data;
        }
      }

      if (photoData?.photo_url || photoData?.photo_storage_path) {
        setCheckInPhoto(photoData);
        setLoadingImage(true);
      }
    } catch (err) {
      console.error('[TimesheetEntryCard] Error fetching photo:', err);
    }
  };

  const handleImageLoad = () => {
    setLoadingImage(false);
  };

  const handleImageError = () => {
    setLoadingImage(false);
    setFailedImage(true);
  };

  const handleRefreshUrl = async () => {
    if (!checkInPhoto?.photo_storage_path) return;

    setRefreshingUrl(true);
    setFailedImage(false);

    try {
      const result = await regeneratePhotoUrl(checkInPhoto.photo_storage_path);
      
      if (result.error) {
        throw result.error;
      }

      setCheckInPhoto(prev => ({
        ...prev,
        photo_url: result.url
      }));
    } catch (err) {
      console.error('[TimesheetEntryCard] Error refreshing photo URL:', err);
      setFailedImage(true);
    } finally {
      setRefreshingUrl(false);
    }
  };

  const renderCheckInPhoto = () => {
    if (!checkInPhoto) return null;

    if (!checkInPhoto.photo_url && !checkInPhoto.photo_storage_path) {
      return (
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon name="Image" size={20} className="text-gray-400" />
        </div>
      );
    }

    if (failedImage || refreshingUrl) {
      return (
        <div className="relative w-16 h-16 bg-gray-100 rounded-lg flex flex-col items-center justify-center gap-1 flex-shrink-0">
          <Icon name="AlertCircle" size={16} className="text-gray-400" />
          {checkInPhoto.photo_storage_path && !refreshingUrl && (
            <button
              onClick={handleRefreshUrl}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              title="Regenerate photo URL"
            >
              Refresh
            </button>
          )}
          {refreshingUrl && (
            <Icon name="Loader" size={12} className="text-gray-400 animate-spin" />
          )}
        </div>
      );
    }

    return (
      <div className="relative w-16 h-16 flex-shrink-0">
        {loadingImage && (
          <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center z-10">
            <Icon name="Loader" size={16} className="text-gray-400 animate-spin" />
          </div>
        )}
        <button
          onClick={() => setSelectedImage(checkInPhoto.photo_url)}
          className="group relative w-full h-full block overflow-hidden rounded-lg border-2 border-gray-200 hover:border-primary-400 transition-all"
          title="Click to view full photo"
        >
          <img
            src={checkInPhoto.photo_url}
            alt="Check-in photo"
            className={`w-full h-full object-cover group-hover:opacity-75 transition-opacity ${
              loadingImage ? 'opacity-0' : 'opacity-100'
            }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all pointer-events-none">
            <Icon 
              name="ZoomIn" 
              size={14} 
              className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </div>
        </button>
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatHours = (hours) => {
    return typeof hours === 'number' ? hours.toFixed(2) : '0.00';
  };

  // Find the contract to get hourly rate and employer info
  const contract = contracts.find(c => c.id === entry.contract_id);
  const hourlyRate = parseFloat(contract?.hourly_rate || 0);
  const amount = (parseFloat(entry.hours_logged || 0) * hourlyRate).toFixed(2);

  const handleCardClick = (e) => {
    // Don't open modal if clicking on interactive elements
    if (e.target.closest('button') || e.target.tagName === 'BUTTON') {
      return;
    }
    setShowDetailModal(true);
  };

  return (
    <>
      <div 
        className="flex items-start justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer"
        onClick={handleCardClick}
      >
      <div className="flex-1 min-w-0">
        {/* Header with Status */}
        <div className="flex items-center space-x-3 mb-2">
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">
              {entry.project?.name || contract?.jobs?.title || 'Project Work'}
            </div>
            <div className="text-sm text-gray-600">
              {entry.employer?.company_name || entry.employer?.full_name || contract?.employer?.company_name || contract?.employer?.full_name}
            </div>
          </div>
          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(entry.status)}`}>
            {entry.status ? entry.status.charAt(0).toUpperCase() + entry.status.slice(1) : 'Unknown'}
          </span>
        </div>

        {/* Click hint */}
        <div className="flex items-center text-xs text-gray-500 mb-2">
          <Icon name="Eye" size={14} className="mr-1" />
          Click to view timeline
        </div>

        {/* Description */}
        {entry.description && (
          <div className="text-sm text-gray-700 mb-2">
            {entry.description}
          </div>
        )}

        {/* Time Range */}
        <div className="flex items-center text-xs text-gray-500 space-x-4">
          <div className="flex items-center">
            <Icon name="Calendar" size={14} className="mr-1" />
            {formatDateTime(entry.start_time)}
          </div>
          {entry.end_time && (
            <>
              <span>→</span>
              <div>{formatDateTime(entry.end_time)}</div>
            </>
          )}
        </div>

        {/* Rejection Reason */}
        {entry.status === 'rejected' && entry.rejection_reason && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            <span className="font-medium">Rejected: </span>
            {entry.rejection_reason}
          </div>
        )}
      </div>

      {/* Hours and Amount */}
      <div className="ml-4 text-right flex-shrink-0">
        <div className="text-2xl font-bold text-gray-900">
          {formatHours(entry.hours_logged)}h
        </div>
        <div className="text-sm font-medium text-gray-600">
          ${amount}
        </div>
        {entry.status === 'approved' && entry.approved_at && (
          <div className="text-xs text-gray-500 mt-1">
            Approved {new Date(entry.approved_at).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>

    {/* Timeline Detail Modal */}
    <Modal
      isOpen={showDetailModal}
      onClose={() => setShowDetailModal(false)}
      title={`Timeline - ${formatDateTime(entry.start_time).split(',')[0]}`}
      size="xl"
    >
      <div className="max-h-[70vh] overflow-y-auto">
        {/* Entry Details in Timeline Format */}
        <div className="space-y-4">
          {/* Summary Header */}
          <div className="pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  {formatDateTime(entry.start_time)} - {entry.end_time ? formatDateTime(entry.end_time) : 'In Progress'}
                </p>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {formatHours(entry.hours_logged)} hours • ${amount}
                </p>
              </div>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                getStatusColor(entry.status)
              }`}>
                {entry.status ? entry.status.charAt(0).toUpperCase() + entry.status.slice(1) : 'Unknown'}
              </span>
            </div>
          </div>

          {/* Entry Card */}
          <div className={`border-l-4 rounded-lg p-4 shadow-sm ${
            entry.status === 'approved' ? 'border-green-500 bg-green-50' :
            entry.status === 'pending' ? 'border-yellow-500 bg-yellow-50' :
            entry.status === 'rejected' ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-gray-50'
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 mr-4">
                {/* Company/Employer */}
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Briefcase" size={16} className="text-gray-600" />
                  <h4 className="font-semibold text-gray-900">
                    {entry.employer?.company_name || entry.employer?.full_name || contract?.employer?.company_name || contract?.employer?.full_name || 'Unknown Employer'}
                  </h4>
                </div>

            {/* Project Name */}
            {(entry.project?.name || contract?.jobs?.title) && (
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Folder" size={14} className="text-gray-600" />
                <span className="text-sm text-gray-700">{entry.project?.name || contract?.jobs?.title}</span>
              </div>
            )}

            {/* Time Information */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Clock" size={16} className="text-gray-600" />
                <span className="font-medium text-gray-700">
                  {formatDateTime(entry.start_time)}
                  {entry.end_time && ` - ${formatDateTime(entry.end_time)}`}
                </span>
                <span className="text-gray-500">
                  ({formatHours(entry.hours_logged)} hours)
                </span>
              </div>
            </div>

            {/* Description */}
            {entry.description && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="FileText" size={14} className="text-gray-600" />
                  <span className="text-xs text-gray-600 font-medium">Description</span>
                </div>
                <div className="text-sm text-gray-700 bg-white rounded p-3 border border-gray-200">
                  {entry.description}
                </div>
              </div>
            )}
              </div>

              {/* Check-in Photo Thumbnail */}
              <div className="flex-shrink-0 ml-auto">
                {renderCheckInPhoto()}
              </div>
            </div>

            {/* Rate and Amount */}
            <div className="grid grid-cols-3 gap-4 p-3 bg-white rounded-lg border border-gray-200">
              <div>
                <p className="text-xs text-gray-500 mb-1">Hours</p>
                <p className="text-sm font-semibold text-gray-900">{formatHours(entry.hours_logged)}h</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Rate</p>
                <p className="text-sm font-semibold text-gray-900">${hourlyRate}/hr</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Amount</p>
                <p className="text-sm font-semibold text-gray-900">${amount}</p>
              </div>
            </div>

            {/* Rejection Reason */}
            {entry.status === 'rejected' && entry.rejection_reason && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs text-red-600 mb-1 font-medium">Rejection Reason</p>
                <p className="text-sm text-red-700">{entry.rejection_reason}</p>
              </div>
            )}

            {/* Approval Information */}
            {entry.status === 'approved' && entry.approved_at && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-600 mb-1 font-medium">Approved</p>
                <p className="text-sm text-green-700">
                  {new Date(entry.approved_at).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>

    {/* Photo Lightbox Modal */}
    {selectedImage && (
      <div onMouseDown={(e) => e.stopPropagation()}>
        <Modal
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          title="Check-in Photo"
          size="lg"
        >
          <div className="flex items-center justify-center p-4" onMouseDown={(e) => e.stopPropagation()}>
            <img
              src={selectedImage}
              alt="Full size check-in"
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
          </div>
        </Modal>
      </div>
    )}
  </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo - only re-render if data actually changed
  return (
    prevProps.entry?.id === nextProps.entry?.id &&
    prevProps.entry?.status === nextProps.entry?.status &&
    prevProps.entry?.hours_logged === nextProps.entry?.hours_logged &&
    prevProps.entry?.approved_at === nextProps.entry?.approved_at &&
    prevProps.contracts?.length === nextProps.contracts?.length
  );
});

TimesheetEntryCard.displayName = 'TimesheetEntryCard';

export default TimesheetEntryCard;
